<template>
  <div
    class="rs-data-table-v2"
    :style="{ background: styleMode === 'default' ? 'white' : 'inherit' }"
    :class="{ 'border-0': hasNoBorder, 'rounded-0': hasNoRounded }"
  >
    <div
      v-if="!noFilter"
      class="filter-bar align-items-end"
      :class="{ 'rounded-0': hasNoRounded }"
      :style="`background:${filterBgColor}`"
    >
      <div class="filter-bar-left">
        <slot :name="slotMap.filterBeforeSearchSlot" />
      </div>

      <div v-if="viewToggler && viewToggler.show">
        <div class="mb-1">
          <label class="label">{{ viewToggler.label || 'Table Configuration' }}</label>
        </div>
        <RsButtonGroup style="margin-right: 5px">
          <RsButton
            v-for="(item) in ([{ icon: 'table', name: 'List View', id: 'list' }, { icon: 'align-justify', name: 'Card View', id: 'card' }])"
            :key="item.id"
            :title="item.name"
            class="border"
            :variant="styleMode === item.id ? 'secondary' : 'light'"
            :icon="item.icon"
            :icon-only="styleMode !== item.id"
            @click="onViewToggle(item.id)"
          >
            {{ item.name }}
          </RsButton>
        </RsButtonGroup>
      </div>

      <RsFormInput
        v-if="fields.some((v) => v.isSearchable)"
        :id="`${id}_data_table_search`"
        v-model="dataFilters.searchValue"
        type="search"
        :placeholder="searchBoxPlaceholder"
        icon="search"
        :debounce="500"
        :label="searchBoxLabel"
        :style="{ width: searchBoxWidth, marginRight: '5px' }"
        class="rs-data-table__filter-bar--search"
        @input="handleDataFiltersChanged()"
      />

      <div class="filter-bar-left flex-grow-1 d-flex align-items-center justify-content-start">
        <slot :name="slotMap.filterLeft" />
      </div>

      <div class="filter-bar-right flex-grow-1 d-flex align-items-center justify-content-end">
        <slot :name="slotMap.filterRight" />
      </div>

      <RsButton
        v-show="hasExport"
        variant="dark"
        icon="file-download"
        title="Export to CSV"
        class="filter-bar-export ml-3"
        @click="exportConfig && exportConfig.hasDateRangeModal ? isExportModalOpen = true : handleExport()"
      >
        Export CSV
      </RsButton>

      <RsButton
        v-show="hasSimpleExport"
        variant="dark"
        icon="file-download"
        title="Export to CSV"
        class="filter-bar-export ml-3"
        @click="handleSimpleExport"
      >
        Export CSV
      </RsButton>
      <div class="ml-3">
        <slot :name="slotMap.filterEnd" />
      </div>
    </div>

    <template
      v-if="styleMode === 'default' || styleMode === 'list'"
    >
      <RsPartialTableHeader
        :id="id"
        :fields="fields"
        :header-bg-color="headerBgColor"
        :radio-all-value="radioAll"
        :checkbox-all-value="checkboxAll"
        :has-details-view="hasDetailsView"
        :has-dropdown="hasDropdown"
        :data-filters="dataFilters"
        :should-show-header="(field: any) => shouldShowHeader(field)"
        @on-radio-all-clicked="handleRadioAllClicked($event.field)"
        @on-checkbox-all-clicked="handleAllItemsChecked()"
        @on-radio-all-change="radioAll = $event.radioAll"
        @on-checkbox-all-change="checkboxAll = $event.checkboxAll"
        @sorting-handler="handleSorting($event.field)"
      />
    </template>

    <div
      v-if="isLoading"
      class="content loading"
      :style="`background: ${styleMode === 'default' && bodyBgColor}`"
    >
      <div class="spin">
        <RsSpin class="mt-5" />
      </div>
    </div>

    <div
      v-else-if="!isLoading && dataList.length === 0"
      class="content no-data"
      :style="`background: ${styleMode === 'default' && bodyBgColor}`"
    >
      <RsNoData :message="noDataMessage" />
    </div>

    <div
      v-else
      class="content"
      :style="`background: ${styleMode === 'default' && bodyBgColor}`"
    >
      <slot :name="slotMap.bulkEdit" />
      <OverlayScrollbars
        ref="scrollbar"
        :events="{ scroll: onScroll }"
      >
        <div>
          <div
            v-if="styleMode === 'card' && viewToggler"
          >
            <RsPartialTableCardRow
              v-for="(item, itemIndex) in dataList"
              :key="`${item.id}_${itemIndex}`"
              class="my-3 mx-3 bg-white"
              :card-title="getPropertyValue(viewToggler.cardTitleField, item)"
              :title-type="viewToggler.cardTitleField.variant === 'clickable' ? 'link' : 'text'"
              :link-config="{ url: getHref(item, viewToggler.cardTitleField), target: viewToggler.cardTitleField.linkTarget ?? '' }"
              :table-props="(cardTableProps as { id: string; fields: IDataTableFieldConfig[]; hasExport: boolean; noFilter: boolean })"
              :list="childDataList[itemIndex]"
              @on-expand-toggle="onCardViewToggle($event, item, itemIndex)"
            >
              <template #thead>
                <RsPartialTableHeader
                  :id="id"
                  class="py-0 bg-white"
                  :fields="fields"
                  :header-bg-color="headerBgColor"
                  :radio-all-value="radioAll"
                  :checkbox-all-value="checkboxAll"
                  :has-details-view="hasDetailsView"
                  :has-dropdown="hasDropdown"
                  :should-show-header="(field: any) => shouldShowHeader(field)"
                  @on-radio-all-clicked="handleRadioAllClicked($event.field)"
                  @on-checkbox-all-clicked="handleAllItemsChecked()"
                  @on-radio-all-change="radioAll = $event.radioAll"
                  @on-checkbox-all-change="checkboxAll = $event.checkboxAll"
                  @sorting-handler="handleSorting($event.field)"
                />
              </template>
              <template #row>
                <RsPartialTableRow
                  :id="id"
                  class="py-0"
                  :row-selectable="rowSelectable"
                  :row-unselected="selectedItemIndex !== null && itemIndex !== selectedItemIndex"
                  :body-bg-color="bodyBgColor"
                  :item="item"
                  :show-checkboxes="showCheckboxes"
                  :fields="fields"
                  :is-checked="checkedItems.some((val) => val[checkboxBoundDataProperty] === item[checkboxBoundDataProperty])"
                  :has-details-view="hasDetailsView"
                  :details-view-type="detailsViewType"
                  :item-index="itemIndex"
                  :detail-slot-map-name="slotMap.details"
                  :dropdown-options="getDropdownOptions(item)"
                  :has-dropdown="hasDropdown"
                  :get-property-value="getPropertyValue"
                  :get-href="getHref"
                  :get-sub-text-value="getSubTextValue"
                  :check-value="(field, item) => checkValue(field, item)"
                  :get-property-classes="(field, item) => getPropertyClasses(field, item)"
                  :is-index-selected="(index) => isIndexSelected(index)"
                  :strip-h-t-m-l-tags="(html) => stripHTMLTags(html)"

                  @on-row-selected="handleRowSelect($event.item, $event.field, $event.itemIndex)"
                  @on-checkbox-change="handleCheckboxChange($event.value, $event.item)"
                  @on-favorite-change="handleFavoriteChange($event.event, $event.item)"
                  @on-switch-change="handleSwitchChanged($event.field, $event.item)"
                  @on-item-click="handleItemClicked($event.item, $event.field, $event.index)"
                  @on-audio-click="$emit(eventsMap.onAudioClicked, $event.item, $event.index)"
                  @on-input-change="onInputChanged($event.item, $event.itemIndex, $event.field, $event.value)"
                  @on-radio-click="handleRadioClicked($event.field, $event.item, $event.itemIndex)"
                  @on-select-change="$emit(eventsMap.onSelectChanged, $event.item, $event.itemIndex, $event.field, $event.value)"
                  @on-details-view-toggle="handleDetailsViewToggle($event.itemIndex, $event.item)"
                />
              </template>

              <template #filterBeforeSearchSlot>
                <slot :name="slotMap.cardFilterBeforeSearchSlot" />
              </template>

              <template #filterLeft>
                <slot :name="slotMap.cardFilterLeft" />
              </template>

              <template #filterRight>
                <slot :name="slotMap.cardFilterRight" />
              </template>

              <template #filterEnd>
                <slot :name="slotMap.cardFilterEnd" />
              </template>
            </RsPartialTableCardRow>
          </div>
          <div
            v-else
          >
            <RsPartialTableRow
              v-for="(item, itemIndex) in dataList"
              :id="id"

              :key="`${item.id}_${itemIndex}`"
              :row-selectable="rowSelectable"
              :row-unselected="selectedItemIndex !== null && itemIndex !== selectedItemIndex"
              :body-bg-color="bodyBgColor"
              :item="item"
              :show-checkboxes="showCheckboxes"
              :fields="fields"
              :is-checked="checkedItems.some((val) => val[checkboxBoundDataProperty] === item[checkboxBoundDataProperty])"
              :has-details-view="hasDetailsView"
              :details-view-type="detailsViewType"
              :item-index="itemIndex"
              :detail-slot-map-name="slotMap.details"
              :dropdown-options="getDropdownOptions(item)"
              :has-dropdown="hasDropdown"
              :get-property-value="getPropertyValue"
              :get-href="getHref"
              :get-sub-text-value="getSubTextValue"
              :check-value="(field, item) => checkValue(field, item)"
              :get-property-classes="(field, item) => getPropertyClasses(field, item)"
              :is-index-selected="(index) => isIndexSelected(index)"
              :strip-h-t-m-l-tags="(html) => stripHTMLTags(html)"

              @on-row-selected="handleRowSelect($event.item, $event.field, $event.itemIndex)"
              @on-checkbox-change="handleCheckboxChange($event.value, $event.item)"
              @on-favorite-change="handleFavoriteChange($event.event, $event.item)"
              @on-switch-change="handleSwitchChanged($event.field, $event.item)"
              @on-item-click="handleItemClicked($event.item, $event.field, $event.index)"
              @on-audio-click="$emit(eventsMap.onAudioClicked, $event.item, $event.index)"
              @on-input-change="onInputChanged($event.item, $event.itemIndex, $event.field, $event.value)"
              @on-radio-click="handleRadioClicked($event.field, $event.item, $event.itemIndex)"
              @on-select-change="$emit(eventsMap.onSelectChanged, $event.item, $event.itemIndex, $event.field, $event.value)"
              @on-details-view-toggle="handleDetailsViewToggle($event.itemIndex, $event.item)"
            />
          </div>
        </div>
        <slot :name="slotMap.tableEnd" />
      </OverlayScrollbars>
    </div>

    <div
      v-if="hasFooter"
      class="footer"
      :class="{ 'rounded-0': hasNoRounded }"
      :style="`background: ${footerBgColor}`"
    >
      <RsPaginationBar
        v-if="pagination && !isLoading"
        v-model="pagination"
        @input="$emit(eventsMap.onPaginationChanged, pagination);"
      />
    </div>

    <RsModal
      v-model="isExportModalOpen"
      title="Pick Date Range to Export"
      size="md"
      lazy
      button-text="Export"
      @submit="isDateRangeValid && handleExport();"
    >
      <div>
        <RsDateRange
          id="dt_date_range"
          :date-from="exportDateMin"
          :date-to="exportDateMax"
          @on-min-date-change="(exportDateMin) = $event"
          @on-max-date-change="(exportDateMax) = $event"
        />
      </div>
    </RsModal>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import { TDataTableVariant } from '@/shared/types/t-data-table-variant';
import {
  IApiResponse,
  IMetaResponse,
  IDataTableFieldConfig,
  pageRange,
  IDropdownOptions,
  IPageRange,
  IQueryCommandOptions,
  Like,
  INestedWhereCommand,
  Or,
  IDataTableRouteQuery,
  IDataTableEmitPayload,
  IDataTableCustomFilter,
  IBaseFieldConfig,
  IDataTableExportConfig,
  IExportConfig,
  GreaterThanOrEqual,
  LessThanOrEqual,
  IWhereCommand,
} from '@/shared';
import RsPartialTableRow from '@/components/RsPartialTableRow.vue';
import RsPartialTableCardRow from '@/components/RsPartialTableCardRow.vue';
import RsPartialTableHeader from '@/components/RsPartialTableHeader.vue';
import { exportToExcel } from '@/shared/helpers/excel-export';

const eventsMap: Record<string, string> = {
  onFiltersChanged: 'on-filters-changed',
  onPaginationChanged: 'on-pagination-changed',
  onItemClicked: 'on-item-clicked',
  onDeleteClicked: 'on-delete-clicked',
  onCheckedItemsChanged: 'on-checked-items-changed',
  onSortingChanged: 'on-sorting-changed',
  onExportClicked: 'on-export-clicked',
  onDetailsViewOpened: 'on-details-view-opened',
  onDetailsViewClosed: 'on-details-view-closed',
  onRowSelected: 'on-row-selected',
  onFavoriteClicked: 'on-favorite-clicked',
  onAudioClicked: 'on-audio-clicked',
  onRadioChanged: 'on-radio-changed',
  onRadioAllChanged: 'on-radio-all-changed',
  onSelectChanged: 'on-select-changed',
  onInputChanged: 'on-input-changed',
  onViewToggle: 'on-view-toggle',
  onScrollEnd: 'on-scroll-end',
};

const slotMap: Record<string, string> = {
  filterBeforeSearchSlot: 'filterBeforeSearchSlot',
  filterLeft: 'filterLeft',
  filterRight: 'filterRight',
  filterEnd: 'filterEnd',
  tableEnd: 'tableEnd',
  bulkEdit: 'bulkEdit',
  details: 'details',

  cardFilterBeforeSearchSlot: 'cardFilterBeforeSearchSlot',
  cardFilterLeft: 'cardFilterLeft',
  cardFilterRight: 'cardFilterRight',
  cardFilterEnd: 'cardFilterEnd',
};

const defaultFilters: IDataTableRouteQuery = {
  searchValue: '',
  limit: '20',
  page: '1',
  orderDir: 'asc',
};

const rowSelectableVariants: TDataTableVariant[] = [
  'text',
  'category',
  'status',
  'date',
  'text-icon',
];

const now = dayjs();

export default defineComponent({
  name: 'RsDataTableTemp',
  components: { RsPartialTableHeader, RsPartialTableCardRow, RsPartialTableRow },
  props: {
    id: {
      type: String,
      required: true,
    },

    cardTableProps: {
      type: Object as () => {id: string, fields: IDataTableFieldConfig[], hasExport: boolean},
    },

    viewToggler: {
      type: Object as () => IDataTableFieldConfig['viewToggler'],
      required: false,
      default: undefined,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    filterOnInit: {
      type: Boolean,
      default: true,
    },

    noFilter: {
      type: Boolean,
      default: false,
    },

    hasFooter: {
      type: Boolean,
      default: true,
    },

    hasSubText: {
      type: Boolean,
      default: false,
    },

    showCheckboxes: {
      type: Boolean,
      default: false,
    },

    checkboxBoundDataProperty: {
      type: String,
      default: 'id',
    },

    exportConfig: {
      type: Object as PropType<IDataTableExportConfig>,
      default: null,
    },

    getCardDataList: {
      // @ts-ignore
      type: Function as () => Function,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: async (item: Record<string, any>) => {}, // { 'id': DataList[] } ----- it will help to open multiple cards at once
    },

    childProps: {
      type: Object as PropType<{ id: string, fields: IDataTableFieldConfig[] }>, // Card Table Props
      default: () => ({ id: '', fields: [] }),
    },

    fields: {
      type: Array as PropType<IDataTableFieldConfig[]>,
      default: () => [],
    },

    dataList: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => [],
    },

    hasSimpleExport: {
      type: Boolean,
      default: false,
    },

    simpleExportFileName: {
      type: String,
      default: 'export',
    },

    dataMeta: {
      type: Object as PropType<IMetaResponse>,
      default: null,
    },

    dropdownItems: {
      type: Array as PropType<IDropdownOptions[]>,
      default: () => [],
    },

    rowSelectable: {
      type: Boolean,
      default: false,
    },

    noDataMessage: {
      type: String,
      default: 'No Results Found',
    },

    searchBoxPlaceholder: {
      type: String,
      default: '',
    },

    searchBoxWidth: {
      type: String,
      default: '',
    },

    searchBoxLabel: {
      type: String,
      default: 'Search',
    },

    hasExport: {
      type: Boolean,
      default: false,
    },

    height: {
      type: Number,
      default: null,
    },

    filterBgColor: {
      type: String,
      default: '#fff',
    },

    headerBgColor: {
      type: String,
      default: '#f8f8f8',
    },

    bodyBgColor: {
      type: String,
      default: '#fff',
    },

    footerBgColor: {
      type: String,
      default: '#fff',
    },

    hasDetailsView: {
      type: Boolean,
      default: false,
    },

    detailsViewType: {
      type: String,
      validator: (val: string) => ['accordion', 'toggle'].includes(val),
      default: 'accordion',
    },

    hasNoBorder: {
      type: Boolean,
      default: false,
    },

    hasNoRounded: {
      type: Boolean,
      default: false,
    },

    logAvailableEvents: {
      type: Boolean,
      default: false,
    },

    selectedItemIndex: {
      type: Number,
      default: null,
    },

    openDetailsByIndex: {
      type: Number,
      default: null,
    },

    defaultMode: {
      type: String,
      default: 'default',
    },
  },

  data() {
    return {
      childDataList: {} as Record<number, Record<string, any>[] | undefined>, // {..., 12: undefined} = loading
      eventsMap,
      slotMap,
      radioAll: null,
      checkboxAll: false,
      isInited: false,
      activeView: 0 as number,
      hasAllItemsChecked: false,
      isExportModalOpen: false,
      styleMode: 'default' as 'default' | 'card' | 'list' | string,
      exportDateMin: dayjs(`${now.year()}-${now.month() + 1}-01`).format('YYYY-MM-DD'),
      exportDateMax: now.format('YYYY-MM-DD'),
      checkedItems: [] as Array<Record<string, number | string | Object | Date>>,
      dataFilters: {
        ...defaultFilters,
        ...{
          orderBy:
            this.fields.find((f: IDataTableFieldConfig) => f.isDefaultSortValue)
              ?.property?.replace('?custom', '') || '',
          orderDir:
            this.fields.find((f: IDataTableFieldConfig) => f.isDefaultSortValue)
              ?.defaultSortValueDir || 'asc',
        },
        ...(this.$route && this.$route.query && this.$route.query),
      } as IDataTableRouteQuery,
      pagination: this.dataMeta ? pageRange(this.dataMeta) : undefined,
      oldSearchValue:
        this.$route && this.$route.query && this.$route.query.searchValue
          ? this.$route.query.searchValue
          : '',
      oldLimit:
        this.$route && this.$route.query && this.$route.query.limit
          ? this.$route.query.limit
          : defaultFilters.limit,
      emitPayload: {} as IDataTableEmitPayload,
      customFilters: [] as IDataTableCustomFilter[],
      detailsViewIndexes: [] as Array<number>,
      detailsViewHeight: 0,
    };
  },

  computed: {
    tableClasses(): Record<string, string> {
      return {};
    },
    jsonFields(): Record<string, string> {
      const values: any = {};
      this.fields.forEach((i: IDataTableFieldConfig) => {
        values[i.display] = i.property.replace('?custom', '');
      });
      return values;
    },

    colspan(): number {
      return (
        this.fields.length
        + (this.dropdownItems.length > 0 ? 1 : 0)
        + (this.showCheckboxes ? 1 : 0)
      );
    },

    getStyle(): Record<string, string> {
      if (!this.height) return {};

      return {
        height: `${this.height}px`,
      };
    },

    hasFilterRightSlot(): boolean {
      return !!this.$slots.filterRight;
    },

    hasFilterBeforeSearchSlot(): boolean {
      return !!this.$slots.filterBeforeSearchSlot;
    },

    hasFilterLeftSlot(): boolean {
      return !!this.$slots.filterLeft;
    },

    hasBulkEditSlot(): boolean {
      return !!this.$slots.bulkEdit;
    },

    hasFilterShelfSlot(): boolean {
      return !!this.$slots.filterShelf;
    },

    hasDropdown(): boolean {
      return this.fields.some((f: IDataTableFieldConfig) => f.variant === 'dropdown') || this.dropdownItems.length > 0;
    },

    checkValue(): (field: IDataTableFieldConfig, item: Record<string, any>) => boolean {
      return (field: IDataTableFieldConfig, item: Record<string, any>) => this.getPropertyValue(field, item) !== 'N/A';
    },

    isDateRangeValid(): boolean {
      if (this.exportConfig?.hasDateRangeModal) {
        return !dayjs(this.exportDateMax).isBefore(dayjs(this.exportDateMin));
      }

      return true;
    },
  },

  watch: {
    pagination() {
      this.handlePaginationChanged();
    },

    openDetailsByIndex() {
      this.triggerDetailsOpen();
    },

    dataFilters: {
      immediate: true,
      deep: true,
      handler() {
        this.handleDataFiltersChanged();
      },
    },
    emitPayload: {
      deep: true,
      immediate: true,
      handler(
        newValue: IDataTableEmitPayload,
        oldValue: IDataTableEmitPayload,
      ) {
        if (this.filterOnInit || this.isInited) {
          this.$emit(eventsMap.onFiltersChanged, this.emitPayload);
        }
      },
    },

    dataMeta() {
      this.pagination = pageRange(this.dataMeta);
    },

    dataList: {
      immediate: true,
      deep: true,
      handler() {
        this.setRadioAllValue();
      },
    },
  },

  mounted() {
    if (this.logAvailableEvents) {
      // eslint-disable-next-line no-console
      console.log('Available events', Object.keys(eventsMap).map((e) => `@${eventsMap[e]}`));

      // eslint-disable-next-line no-console
      console.log('Available slots', Object.keys(slotMap).map((s) => `<template #${slotMap[s]}></template>`));
    }

    if (this.defaultMode) {
      this.styleMode = this.defaultMode;
    }
  },

  methods: {

    onScroll(_instance: unknown, event: Event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target as HTMLElement;
      if (scrollTop + clientHeight >= scrollHeight) {
        this.$emit(eventsMap.onScrollEnd);
      }
    },

    async onCardViewToggle(isExpanded: boolean, item: Record<string, any>, index: number) {
      if (isExpanded) {
        const data = await this.getCardDataList(item);
        this.childDataList[index] = [...data];
      } else this.childDataList[index] = undefined;
      this.$forceUpdate();
    },
    onViewToggle(id: 'default' | 'card' | string) {
      this.$emit(eventsMap.onViewToggle, id);
      this.styleMode = id;
    },
    displayTooltip(tip: string) {
      const tooltip = document.createElement('i');

      tooltip.innerHTML = `
        <i class="fas fa-question-circle">${tip}</i>
      `;
      return tooltip;
    },
    stripHTMLTags(html: string) {
      return html.replace(/<[^>]*>|/g, '');
    },
    handleLongText(html: string) {
      const text = this.stripHTMLTags(html);
      return (text.length > 25) ? `${text.substring(0, 25)}...` : text;
    },
    onScrollBottom() {
      const x = this.$refs.scrollbar as any;
      const viewport = x.osInstance()?.elements().viewport;
      // overlayscrollbars@2 dropped the animated `.scroll()` API — native smooth scroll
      // is the closest equivalent (no custom duration/easing control).
      viewport?.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
    },

    setRadioAllValue() {
      this.radioAll = null;
      (this.fields as IDataTableFieldConfig[]).forEach((f) => {
        if (f.variant === 'radio') {
          const count = this.dataList.filter((d: Record<string, any>) => (
            typeof f.trueValue === 'object'
              ? JSON.stringify(f.radioModelValue!(d) || {}) === JSON.stringify(f.trueValue || {})
              : f.radioModelValue!(d) === f.trueValue
          )).length;

          if (count > 0 && count === this.dataList.length) {
            this.radioAll = f.trueValue;
          }
        }
      });
    },

    shouldShowField(field: IDataTableFieldConfig, item: Record<string, any>) {
      if (field.dynamicIsHidden) {
        return !field.dynamicIsHidden(item);
      }

      if (field.variant === 'dropdown') {
        return false;
      }

      return true;
    },

    shouldShowHeader(field: IDataTableFieldConfig): boolean {
      if (field.dynamicIsHidden) {
        const hiddenFields = this.dataList.filter((v: Record<string, any>) => !this.shouldShowField(field, v));
        return hiddenFields.length !== this.dataList.length;
      }

      if (field.variant === 'dropdown') {
        return false;
      }

      return true;
    },

    getPropertyClasses(field: IDataTableFieldConfig, item: Record<string, any>) {
      const classes = (
        typeof field.inputClasses === 'function'
          ? field.inputClasses(item)
          : (field.inputClasses || [])
      );
      return classes;
    },

    getPropertyValue(field: IDataTableFieldConfig, item: Record<string, any>) {
      if (!field.property.endsWith('?custom')) {
        const objectPath = field.property.split('.');

        let value: any = item;

        objectPath.forEach((p: string) => {
          value = value ? value[p] : '';
        });

        if (value !== 0 && value) {
          return value;
        }
      }

      if (field.valueIfPropertyValueIsNull) {
        return field.valueIfPropertyValueIsNull(item);
      }

      if (field.property.replace('?custom', '') === 'category' && item.category === null) {
        return 'N/A';
      }

      return '';
    },

    getSubTextValue(field: IDataTableFieldConfig, item: Record<string, any>) {
      if (field.hasSubText) {
        const objectPath: string[] = field.subTextValue?.split('.') || [];
        let value = '';
        const obj = { ...item };
        let finishedValue: any;

        objectPath.forEach((p: string) => {
          if (obj[p] && obj[p] !== undefined) {
            value = value.concat(`${obj[p]} `);
          }
        });

        return value;
      }
      return '';
    },

    getHref(item: Record<string, any>, field: IDataTableFieldConfig): string {
      if (field.dynamicHref) {
        return field.dynamicHref(item);
      }
      return (field.href || '').replace(':param', item.id);
    },

    getDropdownOptions(item: Record<string, any>) {
      const field: IDataTableFieldConfig = this.fields.find((f: IDataTableFieldConfig) => f.variant === 'dropdown')!;
      return (field ? field.dropdownOptions!(item) : this.dropdownItems.map((d: IDropdownOptions) => ({ ...d, ...{ data: item } }))) as IDropdownOptions[];
    },

    handleItemClicked(item: any, field: any, index: any) {
      this.$emit(eventsMap.onItemClicked, item, field, index);
    },

    handleDeleteClicked(item: Record<string, any>, index: number) {
      this.$emit(eventsMap.onDeleteClicked, item, index);
    },

    handleRowSelect(item: any, field: IDataTableFieldConfig, index: number) {
      if (this.rowSelectable && rowSelectableVariants.includes(field.variant as TDataTableVariant)) {
        this.$emit(eventsMap.onRowSelected, item, index);
      }
    },

    handleAllItemsChecked(forceOpen?: boolean) {
      if (forceOpen) {
        this.checkedItems = this.dataList;
      } else {
        this.checkedItems = [];
      }

      this.checkedItems = this.checkboxAll ? this.dataList : [];
      this.$emit(
        eventsMap.onCheckedItemsChanged,
        this.checkedItems,
      );
    },

    handleCheckboxChange(value: boolean, item: Record<string, string | number | Object | Date>) {
      this.checkedItems = value
        ? [ ...this.checkedItems, item ]
        : this.checkedItems.filter((val) => val[this.checkboxBoundDataProperty] !== item[this.checkboxBoundDataProperty]);
      this.checkboxAll = this.checkedItems.length === this.dataList.length;
      this.$emit(
        eventsMap.onCheckedItemsChanged,
        this.checkedItems,
      );
    },

    handleDataFiltersChanged() {
      this.emitPayload = this.createFilters();
    },

    handlePaginationChanged() {
      if (this.dataFilters.page !== `${this.pagination?.currentPage}`) {
        this.dataFilters.page = `${this.pagination?.currentPage}`;
      }

      if (this.dataFilters.limit !== `${this.pagination?.recordsPerPage}`) {
        this.dataFilters.limit = `${this.pagination?.recordsPerPage}`;
      }
    },

    handleSorting(field: IDataTableFieldConfig) {
      if (field.isSortable) {
        if (this.dataFilters.orderBy === field.property.replace('?custom', '')) {
          this.dataFilters.orderDir = this.dataFilters.orderDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.dataFilters.orderBy = field.property.replace('?custom', '');
          this.dataFilters.orderDir = 'asc';
        }
        this.$emit(eventsMap.onSortingChanged, this.dataFilters.orderBy, this.dataFilters.orderDir);
      }
    },

    handleExport() {
      const queryCommands: IQueryCommandOptions = this.createFilters().filters;

      if (this.exportConfig?.hasDateRangeModal) {
        queryCommands.where = (queryCommands.where || []).filter(
          (w) => (w as IWhereCommand).field !== this.exportConfig.dateRangeFilterProperty,
        );

        queryCommands.where.push(GreaterThanOrEqual(this.exportConfig.dateRangeFilterProperty, this.exportDateMin));
        queryCommands.where.push(LessThanOrEqual(this.exportConfig.dateRangeFilterProperty, this.exportDateMax));
      }

      this.$emit(eventsMap.onExportClicked, {
        fields: this.fields.filter((f: IDataTableFieldConfig) => !f.hideFromExport).map((f: IDataTableFieldConfig) => ({
          property: f.property.replace('?custom', ''),
          display: f.display,
          trueValue: f.trueValue || null,
          falseValue: f.falseValue || null,
          csvCustomTemplate: f.csvCustomTemplate || null,
        }) as IBaseFieldConfig),
        filters: {
          ...queryCommands,
          ...this.dataMeta?.totalRecords && { limit: this.dataMeta.totalRecords },
        },
        ...this.exportConfig?.hasDateRangeModal && {
          dateRange: `${this.exportDateMin}_to_${this.exportDateMax}`,
        },
      } as unknown as IExportConfig);

      this.isExportModalOpen = false;
    },
    handleSimpleExport(): void {
      exportToExcel(this.dataList, this.jsonFields, `${this.simpleExportFileName}.csv`, 'csv');
    },
    handleFavoriteChange(event: Event, item: any) {
      this.$rsGlobals.$rsSwal.warning({
        title: item.featured ? 'Featured Property' : 'Turn Off Featured Property',
        text: item.featured ? 'Are you sure you would like to feature this property on your website?' : 'Are you sure you want to remove this property from the featured section of your website?',
        confirmHandler: () => {
          this.$emit(eventsMap.onFavoriteClicked, item);
        },
        rejectHandler: () => {
          const el: HTMLInputElement | null = document.querySelector(`#switch_data_table_favorite_${item.id}`);
          if (!el) return;
          el.checked = !el.checked;
        },
      });
    },
    handleSwitchChanged(field: IDataTableFieldConfig, item: any) {
      const eventName = `on-${(field.property.replace('?custom', ''))}-changed`;
      this.$emit(eventName, item);
    },

    handleRadioClicked(field: IDataTableFieldConfig, item: Record<string, any>, itemIndex: number) {
      this.$emit(eventsMap.onRadioChanged, item, field.trueValue);
    },

    handleRadioAllClicked(field: IDataTableFieldConfig) {
      this.$emit(eventsMap.onRadioAllChanged, field, this.radioAll);
    },

    onInputChanged(item: string, itemIndex: number, fieldName: IDataTableFieldConfig, newValue: string) {
      this.$emit(eventsMap.onInputChanged, item, itemIndex, fieldName, newValue);
    },

    async handleDetailsViewToggle(index: number, item: any) {
      if (this.isIndexSelected(index)) {
        this.detailsViewIndexes = this.detailsViewIndexes.filter((d) => d !== index);
        this.$emit(eventsMap.onDetailsViewClosed, { index, item });
      } else {
        this.detailsViewIndexes = this.detailsViewType === 'accordion' ? [index] : [ ...this.detailsViewIndexes, index ];
        this.$emit(eventsMap.onDetailsViewOpened, { index, item });
      }
    },

    async handleDetailsViewOpenCloseAll(index: number, item: Record<string, any>, type: string) {
      if (type === 'close') {
        this.detailsViewIndexes = this.detailsViewIndexes.filter((d) => d !== index);
        this.$emit(eventsMap.onDetailsViewClosed, { index, item });
      } else {
        this.detailsViewIndexes = this.detailsViewType === 'accordion' ? [index] : [ ...this.detailsViewIndexes, index ];
        this.$emit(eventsMap.onDetailsViewOpened, { index, item });
      }
    },

    isIndexSelected(index: number) {
      return this.detailsViewIndexes.includes(index);
    },

    createFilters(): { filters: IQueryCommandOptions, query: Record<string, string | number | undefined> } {
      const filters: IQueryCommandOptions = {
        limit: parseInt(this.dataFilters.limit!, 10),
        page: parseInt(this.dataFilters.page!, 10),
        ...(this.dataFilters.orderBy && {
          orderBy: [
            {
              field: this.dataFilters.orderBy,
              direction: this.dataFilters.orderDir || 'asc',
            },
          ],
        }),
      };

      const nestedFilter = (this.fields as IDataTableFieldConfig[]).some(
        (f) => f.isSearchable,
      )
        ? ({
          condition: 'and',
          whereCommands: [],
        } as INestedWhereCommand)
        : undefined;

      if (nestedFilter) {
        this.fields.forEach((f: IDataTableFieldConfig, idx: number) => {
          if (f.isSearchable) {
            nestedFilter.whereCommands.push({
              field: f.property.replace('?custom', ''),
              operator: 'like',
              condition: 'or',
              value: this.dataFilters.searchValue || '',
              notOperator: false,
            });
          }
        });

        filters.where = [
          ...(filters.where ? filters.where : []),
          ...[nestedFilter],
        ];
      }

      this.customFilters.forEach((c: IDataTableCustomFilter) => {
        if (c.value) {
          filters.where = [
            ...(filters.where ? filters.where : []),
            ...[c.command],
          ];
        }

        if (this.dataFilters[c.property.replace('?custom', '')] !== c.value) {
          this.dataFilters.page = '1';
        }

        this.dataFilters[c.property.replace('?custom', '')] = c.value;
      });

      if (this.oldSearchValue !== this.dataFilters.searchValue) {
        this.dataFilters.page = '1';
        this.oldSearchValue = this.dataFilters.searchValue as string;
      }

      if (this.oldLimit !== this.dataFilters.limit) {
        this.dataFilters.page = '1';
        this.oldLimit = this.dataFilters.limit;
      }

      return { filters, query: this.dataFilters };
    },

    appendCustomFilter(filter: IDataTableCustomFilter) {
      const idx = this.customFilters.findIndex(
        (c: IDataTableCustomFilter) => c.customFilterId === filter.customFilterId,
      );

      if (idx !== -1) {
        this.customFilters[idx] = filter;
      } else {
        this.customFilters.push(filter);
      }
    },

    // Removes custom filter based on set customFilterId
    removeCustomFilter(customFilterId: string) {
      const temp = this.customFilters.filter((item) => item.customFilterId !== customFilterId);
      this.customFilters = temp;
    },

    triggerDataFilterUpdate() {
      this.isInited = true;
      this.handleDataFiltersChanged();
    },

    triggerDetailsClose() {
      this.detailsViewIndexes = [];
    },

    triggerDetailsOpen() {
      this.detailsViewIndexes = [ ...this.detailsViewIndexes, this.openDetailsByIndex ];
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/data-table.scss';
</style>
